import { Box, CircularProgress, IconButton } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useEffect, useMemo, useState } from 'react';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { useRoomContext } from 'contexts/RoomContext';
import { useFlash } from 'contexts/flash';
import { createRoomIssueSession, notifyRoomIssue, updateRoomIssueSession } from 'services/report';
import { getInspectionLocationById } from 'services/startInspection';
import { PhotoManagement } from 'app/pages/ValidationsPage/components/elements/PhotoManagement';
import type { AdHocSession } from 'types/report';
import { IssueAssignmentFields } from './IssueAssignmentFields';
import { IssueNotifyModal } from './IssueNotifyModal';
import { IssueSessionsSection } from './IssueSessionsSection';
import { RoomIssueLegacyFields } from './RoomIssueLegacyFields';
import {
  IssueDialogSection,
  IssueMetaGrid,
  IssuePhotosGrid,
  IssueWorkflowDialogLayout
} from './IssueWorkflowDialogLayout';
import { useRoomIssueDialog } from '../hooks/useRoomIssueDialog';
import {
  filterIssueNotificationUsers,
  resolveIssueNotificationClientId
} from '../utils/issueNotifyUsers';

export const RoomIssueDialog = ({
  isOpen,
  issueId,
  submitIdHint,
  clientIdHint,
  onClose,
  onSaved
}: {
  isOpen: boolean;
  issueId?: number;
  submitIdHint?: number;
  clientIdHint?: number;
  onClose: () => void;
  onSaved?: () => void;
}) => {
  const { users } = useRoomContext();
  const { setFlash } = useFlash();
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [selectedNotificationUsers, setSelectedNotificationUsers] = useState<number[]>([]);
  const [selectedNotificationChannels, setSelectedNotificationChannels] = useState<string[]>([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [submitClientId, setSubmitClientId] = useState<number | undefined>();

  const {
    loading,
    issue,
    isEditing,
    issuesConfig,
    criticalityLevels,
    submitId,
    legacyForm,
    assignmentForm,
    existingBeforePhotos,
    existingAfterPhotos,
    newBeforePhotos,
    setNewBeforePhotos,
    newAfterPhotos,
    setNewAfterPhotos,
    archivedPhotoIds,
    setArchivedPhotoIds,
    handleSave,
    setIssue
  } = useRoomIssueDialog({ isOpen, issueId, submitIdHint, onSaved });

  const issueClientId = useMemo(
    () =>
      resolveIssueNotificationClientId(issue, {
        clientId: clientIdHint,
        submitClientId
      }),
    [issue, clientIdHint, submitClientId]
  );

  useEffect(() => {
    if (!isOpen) {
      setSubmitClientId(undefined);
      return;
    }
    if (resolveIssueNotificationClientId(issue, { clientId: clientIdHint })) {
      setSubmitClientId(undefined);
      return;
    }
    const submitToLoad = submitId ?? submitIdHint;
    if (!submitToLoad) return;

    let cancelled = false;
    getInspectionLocationById(submitToLoad)
      .then((res) => {
        if (cancelled) return;
        const loaded =
          res?.submittedLocation?.clientId ?? res?.submittedLocation?.client?.id ?? undefined;
        setSubmitClientId(loaded != null ? Number(loaded) : undefined);
      })
      .catch(() => {
        if (!cancelled) setSubmitClientId(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, issue, clientIdHint, submitId, submitIdHint]);

  const notificationUserOptions = useMemo(
    () => filterIssueNotificationUsers(users, issueClientId),
    [users, issueClientId]
  );

  useEffect(() => {
    const allowedIds = new Set(notificationUserOptions.map((u) => Number(u.value)));
    setSelectedNotificationUsers((prev) => prev.filter((id) => allowedIds.has(Number(id))));
  }, [notificationUserOptions]);

  useEffect(() => {
    if (!isOpen) {
      setIsNotifyModalOpen(false);
      setNotificationMessage('');
      setSelectedNotificationUsers([]);
      setSelectedNotificationChannels([]);
    }
  }, [isOpen]);

  const handleSendManualNotification = async () => {
    if (!submitId || !issueId) return;
    if (!selectedNotificationUsers.length || !selectedNotificationChannels.length) {
      setFlash({ type: 'error', message: 'Select users and channels.' });
      return;
    }
    setIsSendingNotification(true);
    try {
      const res = await notifyRoomIssue(submitId, issueId, {
        userIds: selectedNotificationUsers,
        channels: selectedNotificationChannels,
        message: notificationMessage
      });
      setFlash({ type: 'success', message: res?.message || 'Notifications sent.' });
      setIsNotifyModalOpen(false);
      setNotificationMessage('');
      setSelectedNotificationUsers([]);
      setSelectedNotificationChannels([]);
    } catch {
      setFlash({ type: 'error', message: 'Failed to send notifications.' });
    } finally {
      setIsSendingNotification(false);
    }
  };

  const sessions = (issue?.sessions as AdHocSession[] | undefined) ?? [];
  const locationName =
    issue?.room?.name ||
    (issue as { submitted?: { room?: { name?: string } } })?.submitted?.room?.name;

  return (
    <>
      <DialogConfirm
        isOpen={isOpen}
        onClose={onClose}
        textTitle="Location Issue"
        textButton={isEditing ? 'Save' : 'Edit'}
        textCancel="Close"
        onSubmit={handleSave}
        isIconClose
        maxWidth={1280}
        headerActions={
          <IconButton
            onClick={() => setIsNotifyModalOpen(true)}
            sx={{ color: '#2B3492', p: 0.5 }}
            title="Manual Notification">
            <NotificationsActiveIcon />
          </IconButton>
        }>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <IssueWorkflowDialogLayout
            main={
              <>
                <IssueDialogSection title="Issue Details">
                  <IssueMetaGrid
                    items={[
                      {
                        label: 'Client',
                        value: issue?.room?.client?.clientName
                      },
                      { label: 'Location', value: locationName },
                      { label: 'Assigned By', value: issue?.assignedByUser?.name },
                      { label: 'Reported By', value: issue?.reportedByUser?.name }
                    ]}
                  />
                  <RoomIssueLegacyFields form={legacyForm} isEditing={isEditing} />
                </IssueDialogSection>

                <IssueDialogSection title="Assignment Information">
                  <IssueAssignmentFields
                    control={assignmentForm.control}
                    isEditing={isEditing}
                    users={users || []}
                    criticalityLevels={criticalityLevels}
                  />
                </IssueDialogSection>

                <IssueDialogSection title="Photo Documentation">
                  <IssuePhotosGrid>
                    <PhotoManagement
                      sectionLabel="Issue Photos Before"
                      existingPhotos={existingBeforePhotos}
                      newPhotos={newBeforePhotos}
                      deletedPhotoIds={archivedPhotoIds}
                      disabled={!isEditing}
                      onAddPhotos={(files) => setNewBeforePhotos((prev) => [...prev, ...files])}
                      onDeleteExistingPhoto={(photoId) =>
                        photoId > 0 &&
                        setArchivedPhotoIds((prev) =>
                          prev.includes(photoId) ? prev : [...prev, photoId]
                        )
                      }
                      onRestorePhoto={(photoId) =>
                        setArchivedPhotoIds((prev) => prev.filter((id) => id !== photoId))
                      }
                      onRemoveNewPhoto={(index) =>
                        setNewBeforePhotos((prev) => prev.filter((_, i) => i !== index))
                      }
                      compactUpload
                    />
                    <PhotoManagement
                      sectionLabel="Issue Photos After"
                      existingPhotos={existingAfterPhotos}
                      newPhotos={newAfterPhotos}
                      deletedPhotoIds={archivedPhotoIds}
                      disabled={!isEditing}
                      onAddPhotos={(files) => setNewAfterPhotos((prev) => [...prev, ...files])}
                      onDeleteExistingPhoto={(photoId) =>
                        photoId > 0 &&
                        setArchivedPhotoIds((prev) =>
                          prev.includes(photoId) ? prev : [...prev, photoId]
                        )
                      }
                      onRestorePhoto={(photoId) =>
                        setArchivedPhotoIds((prev) => prev.filter((id) => id !== photoId))
                      }
                      onRemoveNewPhoto={(index) =>
                        setNewAfterPhotos((prev) => prev.filter((_, i) => i !== index))
                      }
                      compactUpload
                    />
                  </IssuePhotosGrid>
                </IssueDialogSection>
              </>
            }
            sessions={
              submitId && issueId ? (
                <IssueSessionsSection
                  sessions={sessions}
                  users={users || []}
                  allowCreate
                  onCreateSession={async (payload) => {
                    const res = await createRoomIssueSession(submitId, issueId, payload);
                    setIssue((prev) =>
                      prev ? { ...prev, sessions: [...(prev.sessions || []), res.session] } : prev
                    );
                    setFlash({ type: 'success', message: 'Session created.' });
                  }}
                  onSaveSession={async (sessionId, payload) => {
                    const res = await updateRoomIssueSession(submitId, issueId, sessionId, payload);
                    setIssue((prev) => {
                      if (!prev?.sessions) return prev;
                      return {
                        ...prev,
                        sessions: (prev.sessions as AdHocSession[]).map((s) =>
                          s.id === res.session.id ? res.session : s
                        )
                      };
                    });
                    setFlash({ type: 'success', message: 'Session updated.' });
                  }}
                />
              ) : undefined
            }
          />
        )}
      </DialogConfirm>

      <IssueNotifyModal
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        onSubmit={handleSendManualNotification}
        disabled={isSendingNotification}
        issuesConfig={issuesConfig}
        userOptions={notificationUserOptions}
        selectedUserIds={selectedNotificationUsers}
        onSelectedUserIdsChange={setSelectedNotificationUsers}
        selectedChannels={selectedNotificationChannels}
        onSelectedChannelsChange={setSelectedNotificationChannels}
        message={notificationMessage}
        onMessageChange={setNotificationMessage}
      />
    </>
  );
};
